import { useEffect, useMemo, useRef, useState } from 'react'
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api'
import { useTranslation } from 'react-i18next'
import {
  applyOverviewZoom,
  ARUBA_BOUNDS,
  ARUBA_CENTER,
  DEFAULT_ZOOM,
  isWithinArubaBounds,
  MIN_MAP_ZOOM,
} from '../lib/constants'
import { latestSightingByAnimal } from '../lib/mapMarkers'
import type { MapSighting } from '../lib/sightingsApi'

export interface LostMapMarker {
  animal_id: number
  lat: number
  lng: number
}

const darkMapStyle = [
  { elementType: 'geometry', stylers: [{ color: '#0d141c' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#0d141c' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8fa3b8' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#1a2836' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#243041' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#071018' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
]

interface Props {
  highlightedSightingId: number | null
  highlightedLostAnimalId: number | null
  sightings: MapSighting[]
  lostMarkers: LostMapMarker[]
  pickingLocation?: boolean
  onMarkerClick: (animalId: number | null, sightingId: number) => void
  onLocationPicked?: (location: { lat: number; lng: number }) => void
}

function markerSvg(kind: 'lost' | 'sighting' | 'user', ageDays = 0) {
  const freshness = Math.max(0, Math.min(1, 1 - ageDays / 21))
  const color =
    kind === 'lost'
      ? '#ff5c7a'
      : kind === 'user'
        ? '#3ecfcf'
        : freshness > 0.66
          ? '#f0a030'
          : freshness > 0.33
            ? '#d7b457'
            : '#8fa3b8'

  if (kind === 'sighting') {
    // Round “eye” marker — reads as a sighting at a glance.
    const size = Math.round(34 + freshness * 10)
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 48 48">
      <circle cx="24" cy="24" r="21" fill="${color}" stroke="#071018" stroke-width="2.5"/>
      <ellipse cx="24" cy="24" rx="15" ry="10" fill="#f7f4ea" stroke="#071018" stroke-width="1.5"/>
      <circle cx="24" cy="24" r="6.5" fill="#2a4a6a"/>
      <circle cx="24" cy="24" r="3.2" fill="#071018"/>
      <circle cx="26.2" cy="21.8" r="1.8" fill="#ffffff"/>
    </svg>`
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new google.maps.Size(size, size),
      anchor: new google.maps.Point(size / 2, size / 2),
    }
  }

  if (kind === 'lost') {
    // Pin with paw + “?” — clearly a missing pet, not a sighting.
    const size = 42
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${Math.round(size * 1.2)}" viewBox="0 0 40 48">
      <path d="M20 1.5C10.3 1.5 3 9 3 18.2 3 30.2 20 46.5 20 46.5S37 30.2 37 18.2C37 9 29.7 1.5 20 1.5Z" fill="${color}" stroke="#071018" stroke-width="2"/>
      <ellipse cx="20" cy="24" rx="6.2" ry="5" fill="#071018"/>
      <circle cx="12.5" cy="17.5" r="2.6" fill="#071018"/>
      <circle cx="17.2" cy="14.2" r="2.5" fill="#071018"/>
      <circle cx="22.8" cy="14.2" r="2.5" fill="#071018"/>
      <circle cx="27.5" cy="17.5" r="2.6" fill="#071018"/>
      <circle cx="31" cy="9" r="7" fill="#f7f4ea" stroke="#071018" stroke-width="1.8"/>
      <path d="M28.2 7.2c0-1.7 1.3-2.9 2.9-2.9s2.9 1.1 2.9 2.6c0 1.2-.6 1.9-1.6 2.5-.8.5-1.1.9-1.1 1.7v.4h-1.9v-.5c0-1.4.5-2.1 1.5-2.7.7-.5 1.1-.9 1.1-1.5 0-.7-.5-1.2-1.2-1.2-.7 0-1.2.5-1.2 1.2H28.2zm2.1 7.4c-.7 0-1.2-.5-1.2-1.2s.5-1.2 1.2-1.2 1.2.5 1.2 1.2-.5 1.2-1.2 1.2z" fill="#071018"/>
    </svg>`
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new google.maps.Size(size, Math.round(size * 1.2)),
      anchor: new google.maps.Point(size / 2, Math.round(size * 1.2)),
    }
  }

  // User location crosshair.
  const size = 36
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 40 40">
    <circle cx="20" cy="20" r="17" fill="${color}" stroke="#071018" stroke-width="2.5"/>
    <circle cx="20" cy="20" r="5" fill="#071018"/>
    <path d="M20 4v8M20 28v8M4 20h8M28 20h8" stroke="#071018" stroke-width="3" stroke-linecap="round"/>
  </svg>`
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(size, size),
    anchor: new google.maps.Point(size / 2, size / 2),
  }
}

function MapFallback({
  focus,
}: {
  focus?: { id: number; lat: number; lng: number }
}) {
  const { t } = useTranslation()
  return (
    <div className="map-fill map-fallback">
      <div>
        <strong>ARUBA GRID</strong>
        {t('status.mapLoading')}
        <p style={{ marginTop: '0.75rem', maxWidth: 360 }}>
          Set <code>VITE_GOOGLE_MAPS_API_KEY</code> in <code>.env</code> to activate Google Maps.
        </p>
        {focus && (
          <p style={{ color: 'var(--amber)' }}>
            Focus → sighting #{focus.id} @ {focus.lat.toFixed(4)}, {focus.lng.toFixed(4)}
          </p>
        )}
      </div>
    </div>
  )
}

function GoogleRadarMap({
  highlightedSightingId,
  highlightedLostAnimalId,
  sightings,
  lostMarkers,
  pickingLocation = false,
  onMarkerClick,
  onLocationPicked,
}: Props) {
  const { t } = useTranslation()
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string
  const { isLoaded, loadError } = useJsApiLoader({ googleMapsApiKey: apiKey })
  const [map, setMap] = useState<google.maps.Map | null>(null)
  const [userLocation, setUserLocation] = useState<google.maps.LatLngLiteral | null>(null)
  const [locationError, setLocationError] = useState(false)
  const [mapType, setMapType] = useState<'roadmap' | 'satellite'>('roadmap')
  const overviewZoomApplied = useRef(false)

  const lostAnimalIds = useMemo(
    () => new Set(lostMarkers.map((marker) => marker.animal_id)),
    [lostMarkers],
  )
  const latestSightings = useMemo(
    () => latestSightingByAnimal(sightings),
    [sightings],
  )
  const visibleSightingMarkers = useMemo(() => {
    const base = latestSightings.filter(
      (s) => s.animal_id == null || !lostAnimalIds.has(s.animal_id),
    )
    if (highlightedSightingId == null) return base
    if (base.some((s) => s.id === highlightedSightingId)) return base
    const selected = sightings.find((s) => s.id === highlightedSightingId)
    return selected ? [...base, selected] : base
  }, [highlightedSightingId, latestSightings, lostAnimalIds, sightings])

  const highlightedSighting = useMemo(
    () =>
      highlightedSightingId == null
        ? undefined
        : sightings.find((s) => s.id === highlightedSightingId),
    [highlightedSightingId, sightings],
  )
  const highlightedLost = useMemo(
    () =>
      highlightedLostAnimalId == null
        ? undefined
        : lostMarkers.find((m) => m.animal_id === highlightedLostAnimalId),
    [highlightedLostAnimalId, lostMarkers],
  )

  useEffect(() => {
    if (!map) return
    const target = highlightedSighting ?? highlightedLost
    if (!target) return
    map.panTo({ lat: target.lat, lng: target.lng })
  }, [highlightedLost, highlightedSighting, map])

  // After bounds clamp the opening zoom, bump in one level (matches “zoom in once”).
  useEffect(() => {
    if (!map || overviewZoomApplied.current) return
    overviewZoomApplied.current = true
    applyOverviewZoom(map)
    // Debug: window.__radarMapZoom() in the browser console
    ;(window as unknown as { __radarMapZoom?: () => number | undefined }).__radarMapZoom =
      () => map.getZoom() ?? undefined
  }, [map])

  // If the browser already granted geolocation earlier, center on the user
  // without prompting. Never call getCurrentPosition when permission is unknown.
  useEffect(() => {
    if (!map) return
    let cancelled = false

    async function restoreKnownLocation() {
      try {
        if (!navigator.geolocation || !navigator.permissions?.query) return
        const status = await navigator.permissions.query({
          name: 'geolocation',
        })
        if (cancelled || status.state !== 'granted') return

        navigator.geolocation.getCurrentPosition(
          ({ coords }) => {
            if (cancelled) return
            const location = {
              lat: coords.latitude,
              lng: coords.longitude,
            }
            setUserLocation(location)
            if (!isWithinArubaBounds(location.lat, location.lng)) return
            map?.panTo(location)
            if (map) applyOverviewZoom(map)
          },
          () => {
            /* keep Aruba default center — do not surface an error on silent restore */
          },
          {
            enableHighAccuracy: false,
            timeout: 8000,
            maximumAge: 60_000,
          },
        )
      } catch {
        /* Permissions API unavailable (e.g. some Safari) — do not prompt */
      }
    }

    void restoreKnownLocation()
    return () => {
      cancelled = true
    }
  }, [map])

  function locateUser() {
    setLocationError(false)
    if (!navigator.geolocation) {
      setLocationError(true)
      return
    }
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const location = { lat: coords.latitude, lng: coords.longitude }
        setUserLocation(location)
        map?.panTo(location)
        map?.setZoom(16)
      },
      () => setLocationError(true),
      { enableHighAccuracy: true, timeout: 10000 },
    )
  }

  function changeZoom(amount: number) {
    map?.setZoom((map.getZoom() ?? DEFAULT_ZOOM) + amount)
  }

  function toggleMapType() {
    const nextType = mapType === 'roadmap' ? 'satellite' : 'roadmap'
    setMapType(nextType)
    map?.setMapTypeId(nextType)
  }

  const fallbackFocus = highlightedSighting
    ? {
        id: highlightedSighting.id,
        lat: highlightedSighting.lat,
        lng: highlightedSighting.lng,
      }
    : highlightedLost
      ? {
          id: highlightedLost.animal_id,
          lat: highlightedLost.lat,
          lng: highlightedLost.lng,
        }
      : undefined

  if (loadError) return <MapFallback focus={fallbackFocus} />
  if (!isLoaded) return <div className="map-fill map-fallback">{t('status.mapLoading')}</div>

  return (
    <div className="map-fill">
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={ARUBA_CENTER}
        zoom={DEFAULT_ZOOM}
        options={{
          styles: darkMapStyle,
          disableDefaultUI: true,
          zoomControl: false,
          mapTypeId: mapType,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          backgroundColor: '#0d141c',
          minZoom: MIN_MAP_ZOOM,
          restriction: {
            latLngBounds: ARUBA_BOUNDS,
            strictBounds: true,
          },
          draggableCursor: pickingLocation ? 'crosshair' : undefined,
        }}
        onLoad={setMap}
        onUnmount={() => setMap(null)}
        onClick={(event) => {
          if (!pickingLocation || !event.latLng) return
          onLocationPicked?.({
            lat: event.latLng.lat(),
            lng: event.latLng.lng(),
          })
        }}
      >
        {visibleSightingMarkers.map((s) => (
          <Marker
            key={s.id}
            position={{ lat: s.lat, lng: s.lng }}
            opacity={
              highlightedSightingId != null && s.id !== highlightedSightingId
                ? 0.7
                : 1
            }
            icon={markerSvg(
              'sighting',
              Math.max(0, (Date.now() - Date.parse(s.sighted_at)) / 86_400_000),
            )}
            onClick={() => onMarkerClick(s.animal_id, s.id)}
            title={`${t('map.latestSighting')} #${s.id}`}
            zIndex={s.id === highlightedSightingId ? 35 : 10}
          />
        ))}
        {lostMarkers.map((s) => (
          <Marker
            key={`lost-${s.animal_id}`}
            position={{ lat: s.lat, lng: s.lng }}
            icon={markerSvg('lost')}
            onClick={() => onMarkerClick(s.animal_id, 0)}
            title={t('map.lostAnimal')}
            zIndex={s.animal_id === highlightedLostAnimalId ? 45 : 40}
            opacity={
              highlightedLostAnimalId != null &&
              s.animal_id !== highlightedLostAnimalId
                ? 0.7
                : 1
            }
          />
        ))}
        {userLocation && (
          <Marker
            position={userLocation}
            icon={markerSvg('user')}
            title={t('map.yourLocation')}
            zIndex={50}
          />
        )}
      </GoogleMap>
      <div className="map-control-stack">
        <button
          type="button"
          className={`map-control-btn${mapType === 'satellite' ? ' active' : ''}`}
          onClick={toggleMapType}
          aria-pressed={mapType === 'satellite'}
          aria-label={t(mapType === 'satellite' ? 'map.viewMap' : 'map.viewSatellite')}
          title={t(mapType === 'satellite' ? 'map.viewMap' : 'map.viewSatellite')}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="m13 7-4-4-4 4 4 4" />
            <path d="m17 11 4 4-4 4-4-4" />
            <path d="m8 12 4 4 6-6-4-4Z" />
            <path d="m16 8 3-3" />
            <path d="M9 21a6 6 0 0 0-6-6" />
            <path d="M9 17a2 2 0 0 0-2-2" />
          </svg>
        </button>
        <button
          type="button"
          className="map-control-btn"
          onClick={() => changeZoom(1)}
          aria-label={t('map.zoomIn')}
          title={t('map.zoomIn')}
        >
          +
        </button>
        <button
          type="button"
          className="map-control-btn"
          onClick={() => changeZoom(-1)}
          aria-label={t('map.zoomOut')}
          title={t('map.zoomOut')}
        >
          −
        </button>
        <button
          type="button"
          className="map-control-btn"
          onClick={locateUser}
          aria-label={t('map.findMe')}
          title={locationError ? t('map.locationUnavailable') : t('map.findMe')}
        >
          ⌖
        </button>
      </div>
    </div>
  )
}

export function RadarMap(props: Props) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined
  const focus =
    props.highlightedSightingId != null
      ? props.sightings.find((s) => s.id === props.highlightedSightingId)
      : props.highlightedLostAnimalId != null
        ? props.lostMarkers.find(
            (m) => m.animal_id === props.highlightedLostAnimalId,
          )
        : undefined

  if (!apiKey) {
    return (
      <MapFallback
        focus={
          focus
            ? {
                id: 'id' in focus ? focus.id : focus.animal_id,
                lat: focus.lat,
                lng: focus.lng,
              }
            : undefined
        }
      />
    )
  }
  return <GoogleRadarMap {...props} />
}
